import { SUPPORT_SERVICE_URL } from '@/config'
import type { SupportEvent } from '@/modules/support/types'
import {
  getAuthorizationHeader,
  streamedSseResponse,
} from '@/services/lukita/utils'
import { captureException } from '@/services/monitoring'

const EVENT_NAMES = new Set([
  'CREATE_TICKET',
  'CREATE_MESSAGE',
  'UPDATE_TICKET',
  'TYPING',
])
const INITIAL_RETRY_DELAY_MS = 1_000
const MAX_RETRY_DELAY_MS = 30_000
// The service pings every 25s, so silence for this long means a dead socket.
export const SILENCE_TIMEOUT_MS = 60_000
// Below this, a connection counts as flapping and the backoff keeps growing.
const STABLE_CONNECTION_MS = 30_000
// A redeploy, a waking tab and a deliberate reconnect all recover on the first
// attempt. Only a stream that stays down this many tries is worth reporting.
const FAILURES_BEFORE_REPORT = 5

interface SupportEventStreamOptions {
  onEvent: (event: SupportEvent) => void
  onResync: () => void
}

const wait = (milliseconds: number) =>
  new Promise((resolve) => setTimeout(resolve, milliseconds))

// Fetch-based SSE (not EventSource) so the Authorization header can be sent.
// The service hub has no replay buffer, so events sent while this was down are
// gone for good. Only a reopen can have missed any, so only a reopen resyncs.
export const openSupportEventStream = ({
  onEvent,
  onResync,
}: SupportEventStreamOptions): (() => void) => {
  let closed = false
  let retryDelay = INITIAL_RETRY_DELAY_MS
  let firstAttempt = true
  let attempt: AbortController | null = null
  let lastActivityAt = Date.now()
  let consecutiveFailures = 0

  // A hidden tab has its timers frozen, so the silence watchdog cannot be
  // trusted to have noticed a socket that died while the user was away.
  const onVisibilityChange = () => {
    if (document.visibilityState !== 'visible') return
    if (Date.now() - lastActivityAt < SILENCE_TIMEOUT_MS) return
    attempt?.abort()
  }

  // Reporting must never break the reconnect loop.
  const reportOutage = (error: unknown) => {
    try {
      captureException(
        error instanceof Error ? error : new Error(String(error)),
      )
    } catch {
      // A broken reporter is not worth losing live updates over.
    }
  }

  const consumeStream = async () => {
    // A fresh controller per attempt. Reusing an aborted one would make every
    // later fetch reject immediately and spin the retry loop.
    const controller = new AbortController()
    attempt = controller
    const isFirstAttempt = firstAttempt
    firstAttempt = false
    let silenceTimer: ReturnType<typeof setTimeout> | undefined

    // Named, or the reconnect loop only ever sees a bare AbortError.
    const restartSilenceTimer = () => {
      clearTimeout(silenceTimer)
      silenceTimer = setTimeout(
        () => controller.abort(new Error('Support event stream went silent')),
        SILENCE_TIMEOUT_MS,
      )
    }

    try {
      // Armed before the request: a proxy that accepts the connection and never
      // answers would otherwise park here forever with nothing to time it out.
      restartSilenceTimer()
      const response = await fetch(`${SUPPORT_SERVICE_URL}/events`, {
        headers: {
          Accept: 'text/event-stream',
          ...(await getAuthorizationHeader()),
        },
        signal: controller.signal,
      })
      if (!response.ok) {
        throw new Error(`Support event stream failed (${response.status})`)
      }

      restartSilenceTimer()
      if (!isFirstAttempt) onResync()

      await streamedSseResponse(response, {
        // Bytes from the service, never a bare 200, are what count as alive.
        onActivity: () => {
          lastActivityAt = Date.now()
          restartSilenceTimer()
        },
        onEvent: ({ name, data }) => {
          if (!name || !EVENT_NAMES.has(name)) return
          onEvent(data as SupportEvent)
        },
      })
    } finally {
      clearTimeout(silenceTimer)
    }
  }

  const runWithReconnect = async () => {
    while (!closed) {
      const startedAt = Date.now()
      let failure: unknown
      try {
        await consumeStream()
        // The service holds the stream open, so a clean end is it dropping us.
        failure = new Error('Support event stream closed unexpectedly')
      } catch (error) {
        failure = error
      }
      if (closed) return

      if (Date.now() - startedAt >= STABLE_CONNECTION_MS) {
        retryDelay = INITIAL_RETRY_DELAY_MS
        consecutiveFailures = 0
      }
      consecutiveFailures += 1
      // Exactly at the threshold, so one outage files one report.
      if (consecutiveFailures === FAILURES_BEFORE_REPORT) reportOutage(failure)

      await wait(retryDelay)
      retryDelay = Math.min(retryDelay * 2, MAX_RETRY_DELAY_MS)
    }
  }

  document.addEventListener('visibilitychange', onVisibilityChange)
  // The loop rejecting means live updates are dead for the session, so at the
  // very least it must not be silent.
  runWithReconnect().catch(reportOutage)

  return () => {
    closed = true
    document.removeEventListener('visibilitychange', onVisibilityChange)
    attempt?.abort()
  }
}
